import React, { useCallback, useState } from "react";
import { notifyError } from "@components/ds/notify";
import { t } from "@constants/localization";
import RerackSheet from "./components/RerackSheet";

// A modal opened while another closes is dropped (Android) or blocked (iOS):
// let the DB sheet / confirm close first.
export const RERACK_SHEET_SWAP_MS = 300;

/**
 * The re-rack gate for moving a picked TP: the server answers
 * TP_RERACK_REQUIRED while the picking DB still holds boxes. The screen
 * closes its sheet and calls `openRerack(conflict, retry)`; the re-rack sheet
 * opens on the boxes to scan back onto a rack and, on Done, `retry` repeats
 * the Change / Remove (which may answer TP_RERACK_REQUIRED again if a box is
 * still out).
 * @returns {{ openRerack: (conflict, retry: () => void, nextName?: string) => void, rerackSheet: React.ReactNode }}
 */
export function useRerackGate() {
  const [gate, setGate] = useState(null);

  const openRerack = useCallback((conflict, retry, nextName = "") => {
    if (!conflict?.customer) {
      // Nothing listed to scan: show the server's reason.
      notifyError(
        conflict?.message || t("DsRouting/errors/tpRerackRequired"),
        "",
      );
      return;
    }
    setTimeout(
      () => setGate({ ...conflict, retry, nextName }),
      RERACK_SHEET_SWAP_MS,
    );
  }, []);

  const onDone = () => {
    const retry = gate?.retry;
    setGate(null);
    if (retry) setTimeout(retry, RERACK_SHEET_SWAP_MS);
  };

  const count = String(gate?.customer?.boxes?.length || 0);
  const notice = gate
    ? gate.nextName
      ? t("DsRouting/handoff/rerackNotice", {
          db: gate.db_name,
          count,
          next: gate.nextName,
        })
      : t("DsRouting/handoff/rerackRemoveNotice", { db: gate.db_name, count })
    : null;

  const rerackSheet = (
    <RerackSheet
      customer={gate?.customer || null}
      notice={notice}
      doneLabel={
        gate
          ? gate.nextName
            ? t("DsRouting/handoff/rerackDoneAssign", { next: gate.nextName })
            : t("DsRouting/handoff/rerackDoneRemove")
          : undefined
      }
      onClose={() => setGate(null)}
      onDone={onDone}
    />
  );

  return { openRerack, rerackSheet };
}
